/* Copyright (c) Trimble Inc. All rights reserved. */

import {
  Breadcrumbs as MuiBreadcrumbs,
  LinkProps as MuiLinkProps,
  BreadcrumbsProps as MuiBreadcrumbsProps,
  useTheme,
  useMediaQuery,
  Link,
  Typography,
  Box,
  Grid,
} from "@material-ui/core";
import ModusIcon from "legacy-components/icons/ModusIcon";
import type { ReactElement } from "react";
import React from "react";
import { useHistory } from "react-router";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";

export type TextType = ReactElement | string | number;

export const toProperText = (text?: string) => {
  if (!text) return "";
  return text.replace(/[-_]/g, " ");
};

export interface LinkProps extends MuiLinkProps {
  text: TextType;
  href?: string;
  params?: any;
}

export interface BreadcrumbProps extends Omit<MuiBreadcrumbsProps, "onClick"> {
  links?: LinkProps[];
  pageTitle?: TextType | null;
  addHome?: boolean;
}

/**
 * @component Breadcrumbs
 * @description The breadcrumb element is a secondary navigation pattern that shows hierarchy among content.
 * @summary [More Details](https://modus.dev.api.trimblecloud.com/?path=/story/base-layout-breadcrumb--default)
 * @param pageTitle (Mandatory)
 * @param links (Optional) should be of type LinkProps
 */
export const Breadcrumbs: React.FC<BreadcrumbProps> = React.memo(
  ({ links, pageTitle, addHome = true, ...props }) => {
    const theme = useTheme();
    const matches = useMediaQuery(theme?.breakpoints?.up("sm"));
    const history = useHistory();
    return (
      <MuiBreadcrumbs
        maxItems={matches ? undefined : 2}
        separator={<ModusIcon name={"chevron_right"} />}
        style={{ paddingBottom: "20px" }}
        {...props}
      >
        {addHome && (
          <Link id={"HOME"} key="-1" onClick={() => history.push("/home")}>
            Home
          </Link>
        )}
        {links?.map((link, index) =>
          link.href !== undefined ? (
            <Link
              key={index}
              onClick={() => link.href && history.push(link.href)}
            >
              {link.text}
            </Link>
          ) : (
            <Typography
              key={index}
              variant="body1"
              style={{ color: "#252A2E" }}
            >
              {link.text}
            </Typography>
          )
        )}
        {pageTitle && (
          <Typography variant="body1" style={{ color: "#252A2E" }}>
            {pageTitle}
          </Typography>
        )}
      </MuiBreadcrumbs>
    );
  }
);

interface BackPageLinkProps {
  customText?: string | ReactElement;
  onlyCustomText?: boolean;
  action?: () => void;
}

/**
 * @component BackPageLink
 * @description The BackPageLink element is a secondary navigation pattern that redirects to previous page
 * @param customText (Optional) - to override default text "Back to"
 * @param action (Optional) to override the default history search
 */
export const BackPageLink: React.FC<BackPageLinkProps> = React.memo(
  ({ customText, action, onlyCustomText = false }) => {
    let state: any;
    const goBack = () => {
      if (action) {
        action();
        return;
      }
      window.history.back();
    };

    return (
      <Box>
        <Link
          color="primary"
          // sx={{ textDecoration: 'none', display: 'inline' }}
          title="Click here to take you to previous page"
          onClick={goBack}
        >
          <Grid direction="row">
            <KeyboardBackspaceIcon
              color="primary"
              onClick={goBack}
              className="back-icon"
              // sx={{ marginTop: '5px', marginRight: '5px', fontSize: '14px'}}
            />
            <Typography variant="body1" color="primary">
              {onlyCustomText ? "" : "Back"}
              {typeof customText === "string" &&
              !onlyCustomText &&
              (customText ?? (state?.parent as string))?.length > 0
                ? " to "
                : ""}
              {typeof customText === "string" &&
                toProperText(customText ?? (state?.parent as string))}
              {typeof customText !== "string" && customText}
            </Typography>
          </Grid>
        </Link>
      </Box>
    );
  }
);
